import React, { useEffect, useState, useMemo, useCallback } from "react";
import { icons } from "../../Assets/Icons/icons";
import { createPortal } from "react-dom";
import SubCategoryDetailsModal from "../../components/AdminSideComponents/AdminSubCategory/SubCategoryDetailsModal";
import axios from "axios";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

// Helper: extract a readable string from { en, hi, es, zh } or plain string
const getLocalizedString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.en || value.hi || value.es || value.zh || Object.values(value)[0] || "";
  }
  return "";
};

const AdminSubCategorySkeleton = ({ count = 8 }) => {
  return (
    <div className="p-6 min-h-[250px] grid xl:grid-cols-2 md:grid-cols-2 grid-cols-1 gap-6">
      {Array(count)
        .fill("")
        .map((_, i) => (
          <div
            key={i}
            className="flex bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 animate-pulse"
          >
            <div className="w-1/2 min-w-[230px] h-auto bg-gray-200"></div>

            <div className="flex-1 p-5 flex flex-col justify-between">
              <div>
                <div className="h-5 bg-gray-200 rounded w-2/3 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mt-4"></div>
              </div>

              <div className="flex gap-3 mt-5">
                <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

const SupplierSubCategories = () => {
  const [subCategoryData, setSubCategoryData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewSubCategory, setViewSubCategory] = useState(null);

  const [entriesPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchSubCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
      );
      setSubCategoryData(res.data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
      setError("Failed to load sub-categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  // Filter search – use localized name
  const filteredSubCategories = useMemo(() => {
    return subCategoryData.filter((sub) => {
      const nameStr = getLocalizedString(sub.name);
      return nameStr.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [subCategoryData, searchQuery]);

  const totalPages = Math.ceil(filteredSubCategories.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(
    startIndex + entriesPerPage,
    filteredSubCategories.length
  );

  const paginatedSubCategories = useMemo(() => {
    return filteredSubCategories.slice(startIndex, endIndex);
  }, [filteredSubCategories, startIndex, endIndex]);

  const renderCard = (sub, i) => (
    <div
      key={sub._id || i}
      className="grid grid-cols-2 h-[220px] bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200"
    >
      <div className="w-full h-auto overflow-hidden">
        <img
          src={
            sub.imgUrl ||
            "https://via.placeholder.com/150x150.png?text=No+Image"
          }
          alt={getLocalizedString(sub.name)}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
        />
      </div>

      <div className="flex-1 p-4 flex flex-col justify-between">
        <div>
          <h3 className="font-medium text-lg text-gray-800 capitalize truncate">
            {getLocalizedString(sub.name)}
          </h3>
          <p className="text-gray-500 text-sm line-clamp-2 mt-2">
          {getLocalizedString(sub.description) || "No description available"}
          </p>
          {sub.category?.name && (
            <span className="inline-block mt-3 text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              {getLocalizedString(sub.category.name)}
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setViewSubCategory(sub)}
            className="flex-1 flex justify-center items-center gap-1 bg-blue-50 text-[#ea8d2a] hover:bg-[#ea8d2a] hover:text-white py-2 rounded-lg font-medium transition"
          >
            {icons.eyeIcon}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section className="min-h-screen p-6 bg-[#EEF3F7]">
        <div className="flex flex-col md:flex-row md:items-center justify-between pb-6">
          <h1 className="text-xl md:text-3xl flex gap-1 items-center font-philosopher text-primaryTextColor">
            <span className="text-primaryColor">{icons.subCategoryIcon}</span>{" "}
            Sub Categories
          </h1>
        </div>

        <div className="">
          {/* Search Bar */}
          <article className="flex flex-col bg-white rounded-lg p-6 md:flex-row justify-between space-y-4 md:space-y-0 md:items-center">
            <div className="relative">
              <input
                type="text"
                placeholder="Search Sub-Category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button className="text-xl hover:text-primaryColor transition-colors duration-500">
                <Search className="absolute right-3 top-2.5 w-5 h-5 text-secondaryTextColor" />
              </button>
            </div>
          </article>

          {/* Content List */}
          <div>
            {loading ? (
              <AdminSubCategorySkeleton />
            ) : error ? (
              <div className="text-center text-red-500 py-10">{error}</div>
            ) : filteredSubCategories.length > 0 ? (
              <div className="my-6 min-h-[250px] grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
                {paginatedSubCategories.map(renderCard)}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <h2 className="text-2xl font-semibold text-gray-500 mb-2">
                  No Subcategories Found
                </h2>
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredSubCategories.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(endIndex, filteredSubCategories.length)} of{" "}
                  {filteredSubCategories.length} entries
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg transition ${
                        currentPage === i + 1
                          ? "bg-blue-500 text-white"
                          : "border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Only view modal */}
      {viewSubCategory &&
        createPortal(
          <SubCategoryDetailsModal
            isOpen={!!viewSubCategory}
            subCategory={viewSubCategory}
            onClose={() => setViewSubCategory(null)}
          />,
          document.body
        )}
    </>
  );
};

export default SupplierSubCategories;